// Packages and componets
import React, { useState } from 'react';
import { Box, Text } from 'grommet';
import { MoonLoader } from 'react-spinners';
import SHDAuthButton from '@ui/AuthButton/SHDAuthButton';
import SHDButton from '@ui/Button/SHDButton';
import SHDFancyRule from '@ui/FancyRule/SHDFancyRule';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDInputPhoneNumber from '@ui/InputPhoneNumber/SHDInputPhoneNumber';

// Helpers and utilities
import { useIsAndroid } from '@hooks/useIsAndroid';
import { theFirebase } from '@services/Firebase/firebase';

// Types
import type { ErrorMessage } from './SignIn.d';

interface Props {
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  handlePhoneSignIn: () => void;
  inviteCode?: string;
  isInvitedUser: boolean;
  error: ErrorMessage | null;
  setError: (err: ErrorMessage | null) => void;
  setLoading: (isLoading: boolean) => void;
  sendSMSLoading: boolean;
  handleAuthentication: () => void;
  headline: string;
  subHeadline?: JSX.Element | undefined;
}

const AlternativeLogin: React.FC<Props> = ({
  phoneNumber,
  setPhoneNumber,
  handlePhoneSignIn,
  handleAuthentication,
  inviteCode,
  isInvitedUser,
  error,
  setError,
  setLoading,
  sendSMSLoading,
  headline,
  subHeadline,
}) => {
  const { isAndroid } = useIsAndroid();
  const { googleProvider, facebookProvider, appleProvider } =
    theFirebase().getAllAuthProviders();
  const [isPossiblePhoneNumber, setIsPossiblePhoneNumber] =
    useState<boolean>(false);

  return (
    <Box width="100%" gap="small">
      <SHDHeading level={3} formatCasing={false}>
        {headline}
      </SHDHeading>
      {subHeadline && <Text>{subHeadline}</Text>}
      <Box margin={{ bottom: 'small' }}>
        <SHDInputPhoneNumber
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          error={error}
          onChange={(value: string) => setPhoneNumber(value)}
          onPossiblePhoneNumber={(isPossibleNum: boolean) => {
            setError(null), setIsPossiblePhoneNumber(isPossibleNum);
          }}
        />
      </Box>

      <SHDButton
        primary
        label={sendSMSLoading ? <Text color="transparent"></Text> : 'Continue'}
        disabled={!isPossiblePhoneNumber}
        icon={sendSMSLoading ? <MoonLoader size={20} /> : undefined}
        onClick={handlePhoneSignIn}
        onError={(err: ErrorMessage) => {
          setError(err);
        }}
      />

      <SHDFancyRule label="or" />

      <Box gap="xxsmall">
        <SHDAuthButton
          firebaseProvider={googleProvider}
          inviteCode={isInvitedUser ? inviteCode : undefined}
          provider="google"
          onAuthenticate={handleAuthentication}
          onError={(err: ErrorMessage) => {
            setError(err);
          }}
          onLoading={(isLoading: boolean) => {
            setLoading(isLoading);
          }}
        />

        <SHDAuthButton
          firebaseProvider={facebookProvider}
          inviteCode={isInvitedUser ? inviteCode : undefined}
          provider="facebook"
          onAuthenticate={handleAuthentication}
          onError={(err: ErrorMessage) => {
            setError(err);
          }}
          onLoading={(isLoading: boolean) => {
            setLoading(isLoading);
          }}
        />

        {!isAndroid && (
          <SHDAuthButton
            firebaseProvider={appleProvider}
            inviteCode={isInvitedUser ? inviteCode : undefined}
            provider="apple"
            onAuthenticate={handleAuthentication}
            onError={(err: ErrorMessage) => {
              setError(err);
            }}
            onLoading={(isLoading: boolean) => {
              setLoading(isLoading);
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default AlternativeLogin;
